import LockIcon from '@/assets/icons/lock.svg?react';
import { cn } from '@/shared/lib/cn';
import { Banner } from '@/shared/ui/banner';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Spinner } from '@/shared/ui/spinner';
import { PADDLE_FRAME_CLASS } from '../constants';
import type { CheckoutPhase } from '../types';

type PaddleFrameProps = {
  phase: CheckoutPhase;
  planName: string;
  email: string;
  onSubscribe: () => void;
};

const NOTE: Record<CheckoutPhase, string> = {
  idle: 'secure, by Paddle',
  starting: 'Loading the secure checkout',
  loading: 'Loading the secure checkout',
  ready: 'secure, by Paddle',
  processing: 'Confirming the payment…',
  declined: 'secure, by Paddle',
  completed: 'secure, by Paddle',
};

/** The area Paddle's inline checkout renders into (design: Checkout, the dashed frame). */
export function PaddleFrame({ phase, planName, email, onSubscribe }: PaddleFrameProps) {
  const isLoading = phase === 'starting' || phase === 'loading';
  const isOpen = phase !== 'idle';
  const isFrameShown = phase === 'ready' || phase === 'processing' || phase === 'declined';

  return (
    <section
      aria-label="Paddle checkout area"
      aria-busy={isLoading || phase === 'processing'}
      className="flex flex-col gap-3 rounded-tile border-[1.5px] border-surface-4 border-dashed bg-surface-2 p-4.5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-semibold">
          <LockIcon className="size-4" />
          Paddle checkout
        </span>
        <span className="inline-flex items-center gap-2 text-meta text-muted">
          {isLoading || phase === 'processing' ? <Spinner size="sm" /> : null}
          {NOTE[phase]}
        </span>
      </div>
      {phase === 'declined' ? (
        <div role="alert">
          <Banner tone="bad" size="small" title="Your card was declined.">
            Nothing was charged. Try another card or payment method.
          </Banner>
        </div>
      ) : null}
      {isFrameShown ? null : (
        <div aria-hidden="true" className="flex flex-col gap-2.5">
          <Skeleton variant="line" isPulsing={isLoading} />
          <div className="flex h-11 items-center rounded-control bg-surface-3 px-3.5">
            {isLoading ? null : <span className="text-meta text-muted">{email}</span>}
          </div>
          <Skeleton variant="line" isPulsing={isLoading} />
          <Skeleton isPulsing={isLoading} />
          <div className="grid grid-cols-2 gap-2.5">
            <Skeleton isPulsing={isLoading} />
            <Skeleton isPulsing={isLoading} />
          </div>
        </div>
      )}
      <div className={cn(PADDLE_FRAME_CLASS, isFrameShown ? 'block' : 'hidden')} />
      {isOpen ? null : (
        <Button variant="primary" size="lg" isBlock onClick={onSubscribe}>
          Subscribe to {planName}
        </Button>
      )}
      <p className="text-meta text-muted">
        Paddle shows the price in your currency and adds tax for your country before you pay. Card
        details go to Paddle, never to Macswarm.
      </p>
    </section>
  );
}
